import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData';
import { showToast } from '@/components/layout/Toast';
import type { DisponibilidadConfig, Agendamiento, Feriado } from '@/lib/types';
import { PageHeader } from '@/components/ui'

// ── Constants ──

const DAY_LABELS = [
  { label: 'Lun', value: 1 },
  { label: 'Mar', value: 2 },
  { label: 'Mié', value: 3 },
  { label: 'Jue', value: 4 },
  { label: 'Vie', value: 5 },
  { label: 'Sáb', value: 6 },
  { label: 'Dom', value: 0 },
];

const ORIGEN_BADGES: Record<string, { bg: string; color: string }> = {
  directo: { bg: 'var(--veil-10)', color: 'var(--text-muted)' },
  campana_email: { bg: 'var(--primary-soft)', color: 'var(--primary-light)' },
  chatbot: { bg: 'var(--info-bg)', color: 'var(--info)' },
  social: { bg: 'var(--success-bg)', color: 'var(--success)' },
};

const ESTADO_BADGES: Record<string, { bg: string; color: string; label: string }> = {
  confirmado: { bg: 'var(--success-bg)', color: 'var(--success)', label: 'Confirmado' },
  cancelado: { bg: 'var(--danger-bg)', color: 'var(--danger)', label: 'Cancelado' },
  completado: { bg: 'var(--info-bg)', color: 'var(--info)', label: 'Completado' },
  no_show: { bg: 'var(--warning-bg)', color: 'var(--warning)', label: 'No Show' },
};

type TabId = 'citas' | 'config' | 'feriados';

const TABS: { id: TabId; label: string; iconId: string }[] = [
  { id: 'citas', label: 'Citas', iconId: 'clipboard' },
  { id: 'config', label: 'Configuración', iconId: 'settings' },
  { id: 'feriados', label: 'Feriados', iconId: 'calendar-off' },
];

// ── SVG Icons ──
const svgProps = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

function Icon({ name, size = 16 }: { name: string; size?: number }) {
  const p = { ...svgProps, width: size, height: size };
  switch (name) {
    case 'calendar': return <svg {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
    case 'calendar-off': return <svg {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="10" y1="15" x2="14" y2="15" /></svg>;
    case 'check': return <svg {...p}><polyline points="20 6 9 17 4 12" /></svg>;
    case 'check-double': return <svg {...p}><path d="M18 6L7 17l-5-5" /><path d="m22 10-9.5 9.5L10 17" /></svg>;
    case 'warning': return <svg {...p}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
    case 'settings': return <svg {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>;
    case 'clipboard': return <svg {...p}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>;
    case 'refresh': return <svg {...p}><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>;
    case 'edit': return <svg {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
    case 'pause': return <svg {...p}><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>;
    case 'play': return <svg {...p}><polygon points="5 3 19 12 5 21 5 3" /></svg>;
    case 'trash': return <svg {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>;
    default: return null;
  }
}

// ── Shared Styles ──

const inputStyle: React.CSSProperties = {
  padding: '10px 14px',
  background: 'var(--bg-input)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text-primary)',
  fontSize: 14,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-secondary)',
  marginBottom: 4,
  display: 'block',
};

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 24,
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 28px',
  background: 'var(--brand-500)',
  color: 'var(--text-on-primary)',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const btnSecondary: React.CSSProperties = {
  padding: '10px 20px',
  background: 'var(--veil-08)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.2s',
};

// ── Component ──

export default function AgendamientoPage() {
  const api = useApi();
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const activeTab: TabId = (tab === 'config' || tab === 'feriados') ? tab : 'citas';

  // ── Config state ──
  const [config, setConfig] = useState<Partial<DisponibilidadConfig>>({
    nombre: '',
    duracion_minutos: 30,
    dias_disponibles: [1, 2, 3, 4, 5],
    hora_inicio: '09:00',
    hora_fin: '18:00',
    buffer_minutos: 15,
    max_dias_adelanto: 30,
    timezone: 'America/Lima',
  });
  const [savingConfig, setSavingConfig] = useState(false);

  // ── Agendamientos state ──
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');

  // ── Feriados state ──
  const [showFeriadoForm, setShowFeriadoForm] = useState(false);
  const [editingFeriado, setEditingFeriado] = useState<Feriado | null>(null);
  const [feriadoForm, setFeriadoForm] = useState({ fecha: '', nombre: '', recurrente: false });
  const [savingFeriado, setSavingFeriado] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // ── Load data on mount ──
  // `config` es un formulario editable, asi que sigue siendo estado local
  // sembrado del servidor; las dos listas van al cache.
  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    try {
      const res = await api.get('/api/booking/config');
      if (res) setConfig(res);
    } catch {
      showToast('Error al cargar configuración', 'error');
    }
  }

  // Los filtros van en la clave: ahora cambiar uno trae la lista sola, en vez
  // de esperar a que alguien pulse el boton de refrescar (que sigue estando).
  const { data: agendamientos = [], isLoading: loadingList, refrescar: loadAgendamientos } = useCachedData(
    ['agendamientos', filtroEstado, filtroDesde, filtroHasta],
    async () => {
      const params = new URLSearchParams();
      if (filtroEstado !== 'todos') params.set('estado', filtroEstado);
      if (filtroDesde) params.set('desde', filtroDesde);
      if (filtroHasta) params.set('hasta', filtroHasta);
      const qs = params.toString();
      const res = await api.get(`/api/booking/agendamientos${qs ? `?${qs}` : ''}`).catch(() => {
        showToast('Error al cargar agendamientos', 'error');
        return [];
      });
      // La API devuelve { agendamiento: {...}, contacto_nombre, contacto_email }
      return Array.isArray(res)
        ? res.map((r: any) => r.agendamiento ? { ...r.agendamiento, contacto_nombre: r.contacto_nombre, contacto_email: r.contacto_email } : r) as Agendamiento[]
        : [];
    },
  );

  const { data: feriadosList = [], isLoading: loadingFeriados, refrescar: loadFeriados } = useCachedData(
    ['feriados'],
    () => api.get('/api/booking/feriados')
      .then(res => (Array.isArray(res) ? res as Feriado[] : []))
      .catch(() => {
        showToast('Error al cargar feriados', 'error');
        return [] as Feriado[];
      }),
  );

  // ── Stats ──
  const stats = useMemo(() => {
    const total = agendamientos.length;
    const confirmados = agendamientos.filter((a) => a.estado === 'confirmado').length;
    const completados = agendamientos.filter((a) => a.estado === 'completado').length;
    const noShow = agendamientos.filter((a) => a.estado === 'no_show').length;
    return { total, confirmados, completados, noShow };
  }, [agendamientos]);


  async function saveConfig() {
    setSavingConfig(true);
    try {
      await api.put('/api/booking/config/admin', config);
      showToast('Configuración guardada', 'success');
    } catch {
      showToast('Error al guardar configuración', 'error');
    } finally {
      setSavingConfig(false);
    }
  }

  async function updateAgendamiento(id: number, estado: string) {
    try {
      await api.put(`/api/booking/agendamientos/${id}`, { estado });
      showToast(`Agendamiento actualizado a ${estado}`, 'success');
      loadAgendamientos();
    } catch {
      showToast('Error al actualizar agendamiento', 'error');
    }
  }

  async function saveFeriado() {
    if (!feriadoForm.fecha || !feriadoForm.nombre.trim()) {
      showToast('Fecha y nombre son requeridos', 'error');
      return;
    }
    setSavingFeriado(true);
    try {
      if (editingFeriado) {
        await api.put(`/api/booking/feriados/${editingFeriado.id}`, feriadoForm);
        showToast('Feriado actualizado', 'success');
      } else {
        await api.post('/api/booking/feriados', feriadoForm);
        showToast('Feriado creado', 'success');
      }
      setShowFeriadoForm(false);
      setEditingFeriado(null);
      setFeriadoForm({ fecha: '', nombre: '', recurrente: false });
      loadFeriados();
    } catch {
      showToast('Error al guardar feriado', 'error');
    } finally {
      setSavingFeriado(false);
    }
  }

  async function deleteFeriado(id: number) {
    setDeletingId(id);
    try {
      await api.del(`/api/booking/feriados/${id}`);
      showToast('Feriado eliminado', 'success');
      loadFeriados();
    } catch {
      showToast('Error al eliminar feriado', 'error');
    } finally {
      setDeletingId(null);
    }
  }

  async function toggleFeriadoActivo(f: Feriado) {
    try {
      await api.put(`/api/booking/feriados/${f.id}`, { activo: !f.activo });
      showToast(f.activo ? 'Feriado desactivado' : 'Feriado activado', 'success');
      loadFeriados();
    } catch {
      showToast('Error al actualizar feriado', 'error');
    }
  }

  function toggleDay(day: number) {
    const current = config.dias_disponibles || [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day];
    setConfig({ ...config, dias_disponibles: next });
  }

  function formatDate(iso: string) {
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }

  function formatTimeRange(inicio: string, fin: string) {
    if (!inicio || !fin) return '-';
    return `${inicio.slice(0, 5)} - ${fin.slice(0, 5)}`;
  }

  function openEditFeriado(f: Feriado) {
    setEditingFeriado(f);
    setFeriadoForm({ fecha: f.fecha, nombre: f.nombre, recurrente: f.recurrente });
    setShowFeriadoForm(true);
  }

  function openNewFeriado() {
    setEditingFeriado(null);
    setFeriadoForm({ fecha: '', nombre: '', recurrente: false });
    setShowFeriadoForm(true);
  }

  // ── Stat Card ──
  function StatCard({ label, value, color, icon }: { label: string; value: number; color: string; icon: React.ReactNode }) {
    return (
      <div
        style={{
          ...cardStyle,
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flex: 1,
          minWidth: 160,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: color.replace(')', ',0.12)').replace('rgb', 'rgba'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
      <PageHeader breadcrumbs={[{ label: 'Marketing' }, { label: 'Agendamiento' }]} />

      {/* ═══ Tabs ═══ */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid var(--border)',
          marginBottom: 24,
        }}
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count =
            tab.id === 'citas' ? stats.total :
            tab.id === 'feriados' ? feriadosList.filter((f) => f.activo).length :
            undefined;

          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.id === 'citas' ? '/marketing/agenda' : `/marketing/agenda/${tab.id}`, { replace: true })}
              style={{
                padding: '12px 24px',
                background: 'none',
                border: 'none',
                borderBottom: `2px solid ${isActive ? 'var(--brand-500)' : 'transparent'}`,
                color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              <Icon name={tab.iconId} size={16} />
              {tab.label}
              {count !== undefined && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 7px',
                    borderRadius: 10,
                    background: isActive ? 'var(--primary-soft)' : 'var(--veil-08)',
                    color: isActive ? 'var(--primary-light)' : 'var(--text-muted)',
                    minWidth: 20,
                    textAlign: 'center',
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ═══ Tab Content ═══ */}
      <div style={{ animation: 'fadeIn 0.2s ease' }}>

        {/* ════════════ TAB: CITAS ════════════ */}
        {activeTab === 'citas' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Stats */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <StatCard label="Total Citas" value={stats.total} color="rgb(116,185,255)" icon={<Icon name="calendar" size={20} />} />
              <StatCard label="Confirmadas" value={stats.confirmados} color="rgb(0,184,148)" icon={<Icon name="check" size={20} />} />
              <StatCard label="Completadas" value={stats.completados} color="rgb(162,155,254)" icon={<Icon name="check-double" size={20} />} />
              <StatCard label="No Show" value={stats.noShow} color="rgb(253,203,110)" icon={<Icon name="warning" size={20} />} />
            </div>

            {/* Filter bar */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={labelStyle}>Estado</label>
                <select
                  style={{ ...inputStyle, minWidth: 160 }}
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="confirmado">Confirmado</option>
                  <option value="cancelado">Cancelado</option>
                  <option value="completado">Completado</option>
                  <option value="no_show">No Show</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Desde</label>
                <input
                  style={inputStyle}
                  type="date"
                  value={filtroDesde}
                  onChange={(e) => setFiltroDesde(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Hasta</label>
                <input
                  style={inputStyle}
                  type="date"
                  value={filtroHasta}
                  onChange={(e) => setFiltroHasta(e.target.value)}
                />
              </div>
              <button onClick={() => void loadAgendamientos()} style={btnSecondary}>
                Filtrar
              </button>
            </div>

            {/* Table */}
            <div style={{ ...cardStyle, padding: 0, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Fecha', 'Hora', 'Nombre', 'Email', 'Empresa', 'Origen', 'Estado', 'Meet', 'Acciones'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '12px 14px',
                          textAlign: 'left',
                          color: 'var(--text-muted)',
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingList ? (
                    <tr>
                      <td colSpan={9} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                        Cargando...
                      </td>
                    </tr>
                  ) : agendamientos.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ padding: 48, textAlign: 'center' }}>
                        <div style={{ marginBottom: 12, opacity: 0.3, color: 'var(--text-muted)' }}><Icon name="calendar" size={32} /></div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No hay agendamientos</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
                          Las citas aparecerán aquí cuando se agenden
                        </div>
                      </td>
                    </tr>
                  ) : (
                    agendamientos.map((a) => {
                      const origenBadge = ORIGEN_BADGES[a.origen] || ORIGEN_BADGES.directo;
                      const estadoBadge = ESTADO_BADGES[a.estado] || ESTADO_BADGES.confirmado;

                      return (
                        <tr
                          key={a.id}
                          style={{
                            borderBottom: '1px solid var(--border)',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--veil-02)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <td style={{ padding: '10px 14px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                            {formatDate(a.fecha)}
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                            {formatTimeRange(a.hora_inicio, a.hora_fin)}
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-primary)', fontWeight: 500 }}>{a.nombre}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{a.email}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{a.empresa || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span
                              style={{
                                padding: '4px 10px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 500,
                                background: origenBadge.bg,
                                color: origenBadge.color,
                              }}
                            >
                              {a.origen}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <span
                              style={{
                                padding: '4px 10px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 500,
                                background: estadoBadge.bg,
                                color: estadoBadge.color,
                              }}
                            >
                              {estadoBadge.label}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            {a.google_meet_url ? (
                              <a
                                href={a.google_meet_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'var(--info)', display: 'inline-flex' }}
                                title="Abrir Google Meet"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                  <polyline points="15 3 21 3 21 9" />
                                  <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                              </a>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                title="Marcar completado"
                                onClick={() => updateAgendamiento(a.id, 'completado')}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'var(--success-bg)',
                                  color: 'var(--success)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                ✓
                              </button>
                              <button
                                title="Marcar no show"
                                onClick={() => updateAgendamiento(a.id, 'no_show')}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'var(--warning-bg)',
                                  color: 'var(--warning)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                ✗
                              </button>
                              <button
                                title="Cancelar"
                                onClick={() => updateAgendamiento(a.id, 'cancelado')}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'var(--danger-bg)',
                                  color: 'var(--danger)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════════════ TAB: CONFIGURACIÓN ════════════ */}
        {activeTab === 'config' && (
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'var(--primary-soft)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                }}
              >
                <Icon name="settings" size={18} />
              </div>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                  Configuración de Disponibilidad
                </h2>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  Define los horarios y días disponibles para agendar citas
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Nombre */}
              <div>
                <label style={labelStyle}>Nombre</label>
                <input
                  style={inputStyle}
                  value={config.nombre || ''}
                  onChange={(e) => setConfig({ ...config, nombre: e.target.value })}
                  placeholder="Ej: Reunión inicial"
                />
              </div>

              {/* Duración */}
              <div>
                <label style={labelStyle}>Duración (minutos)</label>
                <input
                  style={inputStyle}
                  type="number"
                  value={config.duracion_minutos ?? 30}
                  onChange={(e) => setConfig({ ...config, duracion_minutos: Number(e.target.value) })}
                />
              </div>

              {/* Días disponibles – full width */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Días Disponibles</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  {DAY_LABELS.map((d) => {
                    const selected = (config.dias_disponibles || []).includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => toggleDay(d.value)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 20,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 500,
                          transition: 'all 0.2s',
                          background: selected ? 'var(--brand-500)' : 'var(--veil-06)',
                          color: selected ? 'var(--text-on-primary)' : 'var(--text-muted)',
                        }}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Hora inicio */}
              <div>
                <label style={labelStyle}>Hora Inicio</label>
                <input
                  style={inputStyle}
                  type="time"
                  value={config.hora_inicio || '09:00'}
                  onChange={(e) => setConfig({ ...config, hora_inicio: e.target.value })}
                />
              </div>

              {/* Hora fin */}
              <div>
                <label style={labelStyle}>Hora Fin</label>
                <input
                  style={inputStyle}
                  type="time"
                  value={config.hora_fin || '18:00'}
                  onChange={(e) => setConfig({ ...config, hora_fin: e.target.value })}
                />
              </div>

              {/* Buffer */}
              <div>
                <label style={labelStyle}>Buffer entre citas (minutos)</label>
                <input
                  style={inputStyle}
                  type="number"
                  value={config.buffer_minutos ?? 15}
                  onChange={(e) => setConfig({ ...config, buffer_minutos: Number(e.target.value) })}
                />
              </div>

              {/* Max días */}
              <div>
                <label style={labelStyle}>Máximo días de adelanto</label>
                <input
                  style={inputStyle}
                  type="number"
                  value={config.max_dias_adelanto ?? 30}
                  onChange={(e) => setConfig({ ...config, max_dias_adelanto: Number(e.target.value) })}
                />
              </div>

              {/* Timezone */}
              <div>
                <label style={labelStyle}>Zona Horaria</label>
                <input
                  style={inputStyle}
                  value={config.timezone || 'America/Lima'}
                  onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                />
              </div>
            </div>

            {/* Save button */}
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={saveConfig}
                disabled={savingConfig}
                style={{
                  ...btnPrimary,
                  opacity: savingConfig ? 0.6 : 1,
                  cursor: savingConfig ? 'not-allowed' : 'pointer',
                }}
              >
                {savingConfig ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </div>
          </div>
        )}

        {/* ════════════ TAB: FERIADOS ════════════ */}
        {activeTab === 'feriados' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Header + Add button */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 280 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>
                  Feriados y Días No Laborables
                </h2>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  Los feriados bloquean automáticamente el agendamiento de citas en esas fechas
                </p>
              </div>
              <button onClick={openNewFeriado} style={{ ...btnPrimary, flexShrink: 0, whiteSpace: 'nowrap' }}>
                + Agregar Feriado
              </button>
            </div>

            {/* Form (inline) */}
            {showFeriadoForm && (
              <div
                style={{
                  ...cardStyle,
                  border: '1px solid color-mix(in srgb, var(--brand-500) 30%, transparent)',
                  background: 'color-mix(in srgb, var(--primary-light) 6%, transparent)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                    {editingFeriado ? 'Editar Feriado' : 'Nuevo Feriado'}
                  </h3>
                  <button
                    onClick={() => { setShowFeriadoForm(false); setEditingFeriado(null); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 16, alignItems: 'flex-end' }}>
                  <div>
                    <label style={labelStyle}>Fecha</label>
                    <input
                      style={inputStyle}
                      type="date"
                      value={feriadoForm.fecha}
                      onChange={(e) => setFeriadoForm({ ...feriadoForm, fecha: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Nombre</label>
                    <input
                      style={inputStyle}
                      value={feriadoForm.nombre}
                      onChange={(e) => setFeriadoForm({ ...feriadoForm, nombre: e.target.value })}
                      placeholder="Ej: Fiestas Patrias"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingBottom: 2 }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={feriadoForm.recurrente}
                        onChange={(e) => setFeriadoForm({ ...feriadoForm, recurrente: e.target.checked })}
                        style={{ accentColor: 'var(--brand-500)', width: 16, height: 16 }}
                      />
                      Cada año
                    </label>
                    <button
                      onClick={saveFeriado}
                      disabled={savingFeriado}
                      style={{
                        ...btnPrimary,
                        padding: '10px 20px',
                        opacity: savingFeriado ? 0.6 : 1,
                        cursor: savingFeriado ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {savingFeriado ? 'Guardando...' : editingFeriado ? 'Actualizar' : 'Guardar'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Feriados list */}
            <div style={{ ...cardStyle, padding: 0, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Fecha', 'Nombre', 'Tipo', 'Estado', 'Acciones'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '12px 14px',
                          textAlign: 'left',
                          color: 'var(--text-muted)',
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingFeriados ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                        Cargando...
                      </td>
                    </tr>
                  ) : feriadosList.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 48, textAlign: 'center' }}>
                        <div style={{ marginBottom: 12, opacity: 0.3, color: 'var(--text-muted)' }}><Icon name="calendar-off" size={32} /></div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No hay feriados registrados</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
                          Agrega feriados para bloquear automáticamente esas fechas en el agendamiento
                        </div>
                      </td>
                    </tr>
                  ) : (
                    feriadosList.map((f) => {
                      const isPast = new Date(f.fecha + 'T23:59:59') < new Date() && !f.recurrente;
                      return (
                        <tr
                          key={f.id}
                          style={{
                            borderBottom: '1px solid var(--border)',
                            opacity: isPast || !f.activo ? 0.5 : 1,
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--veil-02)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <td style={{ padding: '10px 14px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                            {formatDate(f.fecha)}
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                            {f.nombre}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <span
                              style={{
                                padding: '4px 10px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 500,
                                background: f.recurrente ? 'var(--primary-soft)' : 'var(--veil-08)',
                                color: f.recurrente ? 'var(--primary-light)' : 'var(--text-muted)',
                              }}
                            >
                              {f.recurrente ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="refresh" size={12} /> Cada año</span> : 'Fecha única'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <span
                              style={{
                                padding: '4px 10px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 500,
                                background: f.activo ? 'var(--success-bg)' : 'var(--danger-bg)',
                                color: f.activo ? 'var(--success)' : 'var(--danger)',
                              }}
                            >
                              {f.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                title="Editar"
                                onClick={() => openEditFeriado(f)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'var(--info-bg)',
                                  color: 'var(--info)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                <Icon name="edit" size={14} />
                              </button>
                              <button
                                title={f.activo ? 'Desactivar' : 'Activar'}
                                onClick={() => toggleFeriadoActivo(f)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: f.activo ? 'var(--warning-bg)' : 'var(--success-bg)',
                                  color: f.activo ? 'var(--warning)' : 'var(--success)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                <Icon name={f.activo ? 'pause' : 'play'} size={14} />
                              </button>
                              <button
                                title="Eliminar"
                                onClick={() => {
                                  if (confirm(`¿Eliminar el feriado "${f.nombre}"?`)) {
                                    deleteFeriado(f.id);
                                  }
                                }}
                                disabled={deletingId === f.id}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: deletingId === f.id ? 'not-allowed' : 'pointer',
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'var(--danger-bg)',
                                  color: 'var(--danger)',
                                  opacity: deletingId === f.id ? 0.5 : 1,
                                  transition: 'all 0.15s',
                                }}
                              >
                                <Icon name="trash" size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
